// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { compose, Query, graphql } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faUsersCog } from '@fortawesome/free-solid-svg-icons'
import { difference, intersection } from 'lodash'

import {
  Spinner,
  Warning,
  TriggerableActionItem as ActionItem,
  Card,
  Grid,
  LabelledDetail as Detail,
  Modal,
  SelectEmployerEmployees,
  BulkMessageAudienceMembers,
  KeyValueTable,
  SvgIcon,
  ExpandableSection
} from '../../components'

import { decodeBase64 } from '../../utils'


const QUERY_EMPLOYER_BULKMESSAGE_AUDIENCE = gql`
  query($audienceId: ID!) {
    bulkmessageaudience(id: $audienceId) {
      id
      name
      description
      createdAt
      hasIdentityQueryFilters
      identityQueryFilters
      identities {
        totalCount
        edges {
          node {
            id
            fullName
            identityValue
            msisdnDefault
            client {
              id
              employeeNumber
              employmentType
              status
            }
          }
        }
      }
    }
  }
`


class EmployerBulkSMSAudience extends Component {
  static propTypes = {
    employerId: PropTypes.string.isRequired
  }

  static defaultProps = {
    className: ''
  }

  state = {
    showMembersAddModal: false,
    employees: [],
    selectedRows: [],
    showIdentityQueryFilters: false,
    changeInProgress: false
  }

  componentDidMount() {
    this.refresh()
  }

  refresh = (refetch) => {
    this.setState(
      { changeInProgress: true },
      () => {
        this.props.saveBulkMessageAudience({
          variables: {
            input: {
              id: this.props.match.params.audienceId
            }
          }
        })
        .then(() => {
          this.setState(
            { changeInProgress: false },
            () => refetch
              ? refetch()
              : null
          )
        })
      }
    )
  }

  toggleMembersAddModal = () => (
    this.setState(prevState => (
      { showMembersAddModal: !prevState.showMembersAddModal}
    ))
  )

  addToAudience = (employees, existingMembers) => {
    this.setState(
      { changeInProgress: true, showMembersAddModal: false },
      () => {
        const updatedMembers = [...new Set([...existingMembers, ...employees])]  // using Set like this ensures array will contain unique values
        this.props.saveBulkMessageAudience({
          variables: {
            input: {
              id: this.props.match.params.audienceId,
              identities: updatedMembers.map(employeeId => decodeBase64(employeeId))
            }
          }
        })
        .then(() => this.setState({ changeInProgress: false }))
        .catch(error => this.setState({ changeInProgress: false }))
      }
    )
  }

  removeAudienceMembers = (currentMembers) => {
    this.setState(
      { changeInProgress: true },
      () => {
        const remainingMembers = difference(currentMembers, this.state.selectedRows)

        this.props.saveBulkMessageAudience({
          variables: {
            input: {
              id: this.props.match.params.audienceId,
              identities: remainingMembers.map(employeeId => decodeBase64(employeeId))
            }
          }
        })
        .then(() => {
          this.setState(
            { changeInProgress: false },
            this.clearSelectedRows
          )
        })
        .catch(() => {
          this.setState(
            { changeInProgress: false },
            this.clearSelectedRows
          )
        })
      }
    )
  }

  selectMembersOnPage = (identityIds, refetch) => {
    if (this.state.selectedRows.length === identityIds.length &&
      intersection(this.state.selectedRows, identityIds) === []) {
      // deselect all
      this.setState({ selectedRows: [] }, refetch)
    } else {
      this.setState(
        { changeInProgress: true },
        () => {
          // select all row ids
          this.setState({ selectedRows: identityIds }, refetch)
        }
      )
    }
  }

  selectAllMembers = (identityIds, refetch) => (
    // select all row ids
    this.setState({ selectedRows: identityIds }, refetch)
  )

  clearSelectedRows = () => this.setState({ selectedRows: [] })

  toggleShowFilters = () => (
    this.setState(prevState => (
      { showIdentityQueryFilters: !prevState.showIdentityQueryFilters }
    ))
  )

  render() {
    return (
      <Query
        query={QUERY_EMPLOYER_BULKMESSAGE_AUDIENCE}
        variables={{ audienceId: this.props.match.params.audienceId }}
        fetchPolicy='network-only'>
        {({ loading, error, data, networkStatus, refetch }) => {
          if (loading || networkStatus === 4) {  // networkStatus is 4 when refetching
            return <Spinner type='tail' />
          }

          if (error) {
            return <Warning>Failed to fetch audience data</Warning>
          }

          const bulkMessageAudience = data.bulkmessageaudience
          const currentAudienceMembers = bulkMessageAudience.identities.edges.map(({ node: identity }) => identity.id)
          const hasIdentityQueryFilters = bulkMessageAudience.hasIdentityQueryFilters

          let summaryDetails = [
            <Detail
              label='Name'
              detail={bulkMessageAudience.name}
              detailFontSize="large"
              detailFontWeight="bold"
              className='slds-p-bottom_small'
            />,
            <Detail
              label='Description'
              detail={bulkMessageAudience.description}
              className='slds-p-bottom_small'
            />,
            <Detail
              label='Members'
              detail={bulkMessageAudience.identities
                ? bulkMessageAudience.identities.totalCount
                : '-'
              }
              className='slds-p-bottom_small'
            />,
            <Detail
              label='Created at'
              detail={bulkMessageAudience.createdAt
                ? moment(bulkMessageAudience.createdAt).format('lll')
                : '-'
              }
              className='slds-p-bottom_small'
            />
          ]

          const { client__division__employer__id, ...identityQueryFilters } = JSON.parse(bulkMessageAudience.identityQueryFilters)

          return (
            <div className={`slds-size_1-of-1 ${this.props.className}`}>
              {this.state.changeInProgress &&
                <Spinner type='brand' size='large' />
              }
              {/* Audience overview section */}
              <div className='slds-p-around_large slds-border_bottom'>
                <div className='slds-grid'>
                  <Card
                    title={
                      <div className='slds-grid'>
                        Audience
                        {hasIdentityQueryFilters &&
                          <div className='slds-col_bump-left' title='this is a dynamic audience'>
                            <SvgIcon
                              variant='utility'
                              symbol='magicwand'
                              colour='#5a56fb'
                            />
                          </div>
                        }
                      </div>
                    }
                    icon={
                      <Icon icon={faUsersCog} size='3x' className='slds-m-right_x-small'/>
                    }
                    body={
                      <div>
                        <Grid
                          items={summaryDetails}
                          className='slds-p-bottom_medium'
                        />
                      </div>
                    }
                    footer={identityQueryFilters.length > 0 &&
                      <div style={{ marginTop: '-1em' }}>
                        <ExpandableSection
                          open={this.state.showIdentityQueryFilters}
                          title={
                            <div
                              className='u-font-size-small slds-text-color_weak slds-grid'
                              style={{ alignItems: 'center' }}
                              title={this.state.showIdentityQueryFilters
                                ? 'Hide filters used to create recipient list'
                                : 'Show filters used to create recipient list'
                              }>

                              {this.state.showIdentityQueryFilters
                                ? 'Hide filters'
                                : 'Show filters'
                              }
                              <SvgIcon
                                variant='utility'
                                symbol='info'
                                colour='light'
                                size='small'
                                className='slds-p-left_small'
                                title={this.state.showIdentityQueryFilters
                                  ? 'Hide filters used to create recipient list'
                                  : 'Show filters used to create recipient list'
                                }
                              />
                            </div>
                          }
                          onClick={this.toggleShowFilters}
                          expandIconOnLeft={true}
                          style={{ margin: 0 }}>

                          <KeyValueTable
                            data={identityQueryFilters}
                            keyColumnSize="1-of-2"
                          />
                        </ExpandableSection>
                      </div>
                    }
                    paddingTRL={true}
                    className='slds-size_1-of-2'
                  />
                  <div className='slds-size_1-of-2'>
                    <div className='slds-size_3-of-4 slds-m-left_x-large'>
                      <div className='u-font-size-larger slds-m-bottom_medium'>
                        <b>Actions</b>
                      </div>
                      <ActionItem
                        name='Add new recipients'
                        triggerLabel='add'
                        triggerOnClick={this.toggleMembersAddModal}
                        disabled={hasIdentityQueryFilters}
                        className='slds-m-bottom_small'
                        title={hasIdentityQueryFilters
                          ? 'Adding recipients to dynamic audience not allowed'
                          : null
                        }
                      />
                      <Fragment>
                        <ActionItem
                          name='Remove selected recipients'
                          triggerLabel='remove'
                          triggerOnClick={() => this.removeAudienceMembers(currentAudienceMembers)}
                          disabled={this.state.selectedRows.length === 0 || hasIdentityQueryFilters}
                          className='slds-m-bottom_small'
                          title={hasIdentityQueryFilters
                            ? 'Removing recipients from dynamic audience not allowed'
                            : null
                          }
                        />
                        {bulkMessageAudience.hasIdentityQueryFilters &&
                          <ActionItem
                            name='Refresh recipient listing'
                            triggerLabel='refresh'
                            triggerOnClick={() => this.refresh(refetch)}
                            className='slds-m-bottom_small'
                          />
                        }
                      </Fragment>
                    </div>
                  </div>
                </div>
              </div>
              <BulkMessageAudienceMembers
                audienceId={this.props.match.params.audienceId}
                // TODO: add filters
                // .
                // member selection functions
                selectedRows={this.state.selectedRows}
                onGlobalSelect={(identities) => {
                  if (this.state.selectedRows.length === identities.length) {
                    this.clearSelectedRows()  // deselect all
                    return false  // indicating deselect action
                  } else {
                    this.setState({ selectedRows: identities.map(rr => rr.node.id) })  // select all row ids
                    return true  // indicating select action
                  }
                }}
                onRowSelect={e => {
                  const id = e.target.id.split('-')[1]  // 'checbox-' removed from id (see Checkbox)
                  this.setState(prevState => {
                    if (prevState.selectedRows.includes(id)) {
                      const selectedRows = [...prevState.selectedRows]
                      selectedRows.splice(selectedRows.indexOf(id), 1)  // selected rows minus deselected
                      return { selectedRows }
                    } else {
                      return { selectedRows: [...prevState.selectedRows, id] }
                    }
                  })
                }}
                selectMembersOnPage={(identityIds) => {
                  this.selectMembersOnPage(identityIds, refetch)
                }}
                selectAllMembers={(identityIds) => {
                  this.selectAllMembers(identityIds, refetch)
                }}
                key={`members_${bulkMessageAudience.identities
                  ? bulkMessageAudience.identities.totalCount
                  : 0}`
                }
              />
              {this.state.showMembersAddModal &&
                <Modal
                  body={
                    <SelectEmployerEmployees
                      employerId={this.props.employerId}
                      selected={this.state.employees}
                      onSelectionChange={employees => (
                        this.addToAudience(
                          employees,
                          currentAudienceMembers
                        )
                      )}
                      className='slds-p-top_medium slds-p-bottom_medium slds-p-right_small'
                    />
                  }
                  contentPadding='none'
                  onCloseClick={this.toggleMembersAddModal}
                  sizeLarge={true}
                />
              }
            </div>
          )
        }}
      </Query>
    )
  }
}

const saveBulkMessageAudience = gql`
  mutation ($input: BulkMessageAudienceMutationInput!) {
    bulkmessageaudienceMutate(input: $input) {
      bulkmessageaudience {
        id
        name
        description
        identities {
          totalCount
          edges {
            node {
              id
              fullName
              identityValue
              msisdnDefault
              client {
                id
                employeeNumber
                employmentType
                status
              }
            }
          }
        }
      }
    }
  }
`

const withBulkMessageAudienceMutate =
  graphql(saveBulkMessageAudience, {
    name: 'saveBulkMessageAudience'
  })

export default compose(
  withRouter,
  withBulkMessageAudienceMutate,
)(EmployerBulkSMSAudience)



// WEBPACK FOOTER //
// ./src/containers/messageplus/EmployerBulkSMSAudience.js